import { filterClasses } from "@swivl-mono/helpers";
import { Button, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { FiAlertTriangle, FiMic, FiVolume2 } from "react-icons/fi";

import { AudioDeviceOption, CCAudioDevices } from "../../../Models/ContactCenterV3/CCAudioDevices";
import { CCVoiceDevice } from "../../../Models/ContactCenterV3/CCVoiceDevice";
import ContactCenterV3Model from "../../../Models/ContactCenterV3/ContactCenterV3.Store";

/** Peak below this counts as silence. */
const SILENCE_THRESHOLD = 0.01;
/** How long a manual mic test listens before releasing the microphone. */
const TEST_DURATION_MS = 6000;

/**
 * Microphone and speaker selection for the agent, with a level meter.
 * Lives in Account Settings → Agent Settings (ENG-3018); the compact
 * conversations-page strip it once powered is gone.
 *
 * ── Who owns the microphone ──────────────────────────────────────────────
 * Nothing here captures audio while idle. An earlier version ran the meter
 * permanently, which left Chrome reporting the tab as using the microphone for
 * as long as the Contact Center was open — alarming on an agent console, and it
 * fought with the in-call watch over a single shared meter.
 *
 * So: DURING a call, `CCVoiceDevice` owns the meter and this panel only renders
 * the level it publishes. OUTSIDE a call, nothing is captured until the agent
 * presses Test, which listens briefly and then releases.
 */
export function AudioSettings(): React.ReactElement | null {
  const { voiceState, audioInputLevel } = ContactCenterV3Model.useState();
  const [inputs, setInputs] = useState<AudioDeviceOption[]>([]);
  const [outputs, setOutputs] = useState<AudioDeviceOption[]>([]);
  const [inputId, setInputId] = useState<string | undefined>(undefined);
  const [outputId, setOutputId] = useState<string | undefined>(undefined);
  const [testLevel, setTestLevel] = useState<number | null>(null);
  const [testFailed, setTestFailed] = useState(false);

  const inCall = voiceState === "active" || voiceState === "listening";

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      const { inputs: ins, outputs: outs } = await CCAudioDevices.list();
      if (cancelled) return;
      setInputs(ins);
      setOutputs(outs);
      setInputId(CCAudioDevices.savedInputId ?? ContactCenterV3Model.state.audioInputDeviceId ?? undefined);
      setOutputId(CCAudioDevices.savedOutputId ?? undefined);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Never leave the microphone open behind us.
  useEffect(() => {
    return () => {
      if (!inCall) void CCAudioDevices.stopMeter();
    };
  }, [inCall]);

  const handleInput = async (deviceId: string): Promise<void> => {
    setInputId(deviceId);
    await CCAudioDevices.setInput(CCVoiceDevice.current, deviceId);
    ContactCenterV3Model.setState({ audioInputDeviceId: deviceId, audioInputDead: false });
  };

  const handleOutput = async (deviceId: string): Promise<void> => {
    setOutputId(deviceId);
    await CCAudioDevices.setOutput(CCVoiceDevice.current, deviceId);
  };

  const handleTest = async (): Promise<void> => {
    if (inCall) return;
    setTestFailed(false);
    setTestLevel(0);
    const ok = await CCAudioDevices.startMeter(inputId, (peak) => setTestLevel(peak));
    if (!ok) {
      setTestFailed(true);
      setTestLevel(null);
      return;
    }
    // Release the microphone rather than holding it indefinitely.
    window.setTimeout(() => {
      void CCAudioDevices.stopMeter();
      setTestLevel(null);
    }, TEST_DURATION_MS);
  };

  if (inputs.length === 0) return null;

  const selected = inputs.find((i) => i.deviceId === inputId);
  // In a call the level comes from the watch that owns the meter; outside one it
  // only exists while a test is running.
  const level = inCall ? audioInputLevel : (testLevel ?? 0);
  const showMeter = inCall || testLevel !== null;
  const meterPercent = Math.min(100, Math.round(level * 140));
  const looksSilent = level < SILENCE_THRESHOLD;

  return (
    <div className="flex flex-col gap-2 px-4 py-3">
      <div className="flex items-center gap-2">
        <FiMic size={12} className="shrink-0 text-grey-500" />
        <select
          className="min-w-0 flex-1 rounded border border-grey-200 bg-white px-2 py-1 text-xs text-grey-700"
          value={inputId ?? ""}
          onChange={(e) => void handleInput(e.target.value)}
        >
          {inputs.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label}
              {d.isVirtual ? " (virtual)" : ""}
            </option>
          ))}
        </select>
        {!inCall && (
          <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => void handleTest()}>
            {testLevel !== null ? "Listening…" : "Test"}
          </Button>
        )}
      </div>

      {showMeter && (
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-grey-100">
            <div
              className={filterClasses("h-full rounded-full transition-all duration-75", looksSilent ? "bg-grey-300" : "bg-teal-500")}
              style={{ width: `${meterPercent}%` }}
            />
          </div>
          <span className="w-24 shrink-0 text-right text-[10px] text-grey-400">{looksSilent ? "no sound" : "picking up"}</span>
        </div>
      )}

      {(selected?.isVirtual || testFailed) && (
        <div className="flex items-start gap-1.5 rounded bg-yellow-50 px-2 py-1.5 text-[11px] leading-snug text-yellow-800">
          <FiAlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>
            {testFailed
              ? "This microphone could not be opened — callers will not hear you."
              : "This looks like a virtual audio device. Unless you are deliberately routing audio into it, callers will hear silence."}
          </span>
        </div>
      )}

      {outputs.length > 0 && (
        <div className="flex items-center gap-2">
          <FiVolume2 size={12} className="shrink-0 text-grey-500" />
          <select
            className="min-w-0 flex-1 rounded border border-grey-200 bg-white px-2 py-1 text-xs text-grey-700"
            value={outputId ?? ""}
            onChange={(e) => void handleOutput(e.target.value)}
          >
            {outputs.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
